// src/pages/Stores.tsx
//
// Store-locator. Shows the 6 BENCHMARK partner shops across
// Europe with their contact info, hours, and carried categories.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Card, CardContent } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { EmptyState } from "@/components/ui/EmptyState";
import { DEMO_PARTNER_SHOPS } from "@/lib/demoData";

const ALL_COUNTRIES = Array.from(new Set(DEMO_PARTNER_SHOPS.map((s) => s.country))).sort();

export function Stores() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [countryFilter, setCountryFilter] = useState<string | null>(null);

  const filtered = DEMO_PARTNER_SHOPS.filter((s) => {
    if (countryFilter && s.country !== countryFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        s.name.toLowerCase().includes(q) ||
        s.city.toLowerCase().includes(q) ||
        s.address.some((a) => a.toLowerCase().includes(q))
      );
    }
    return true;
  });

  return (
    <div className="container py-12">
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-3xl font-510 tracking-tight text-fg-primary sm:text-4xl">
          {t("stores.title", "Where to find us")}
        </h1>
        <p className="mt-1 text-sm text-fg-tertiary">
          {t("stores.partnerLine", "{{shops}} partner shops across {{countries}} countries", { shops: DEMO_PARTNER_SHOPS.length, countries: ALL_COUNTRIES.length })}
        </p>
      </header>

      {/* Search + filter */}
      <div className="mb-6 space-y-3">
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("stores.searchPlaceholder", "Search by name or city…")}
        />
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setCountryFilter(null)}
            aria-pressed={!countryFilter}
            className={
              "rounded px-2 py-1 text-xs font-510 transition-colors " +
              (!countryFilter
                ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary")
            }
          >
            {t("catalog.all", "All countries")}
          </button>
          {ALL_COUNTRIES.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCountryFilter(c === countryFilter ? null : c)}
              aria-pressed={countryFilter === c}
              className={
                "rounded px-2 py-1 text-xs font-510 transition-colors " +
                (countryFilter === c
                  ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                  : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary")
              }
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <EmptyState
          title={t("stores.emptyTitle", "No stores match your search")}
          description={t("stores.emptyHint", "Try a different city or country.")}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {filtered.map((shop) => (
            <Card key={shop.id} className="overflow-hidden">
              <CardContent className="space-y-4 p-6">
                {/* Header */}
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-base font-510 tracking-tight text-fg-primary">
                      {shop.name}
                    </h3>
                    <Badge variant="default" size="sm">{shop.country}</Badge>
                  </div>
                  {shop.id === "BENCHMARK-NAMUR" && (
                    <Badge variant="teal" size="sm" className="mt-1.5">
                      HQ
                    </Badge>
                  )}
                </div>

                {/* Contact */}
                <div className="space-y-1.5 text-sm text-fg-secondary">
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-benchmark-teal-bright" strokeWidth={1.5} />
                    <div>
                      {shop.address.join(", ")}
                      <br />
                      {shop.city}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-3.5 w-3.5 flex-shrink-0 text-benchmark-teal-bright" strokeWidth={1.5} />
                    <a href={`tel:${shop.phone.replace(/\s/g, "")}`} className="hover:text-fg-primary">
                      {shop.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 flex-shrink-0 text-benchmark-teal-bright" strokeWidth={1.5} />
                    <a href={`mailto:${shop.email}`} className="truncate hover:text-fg-primary">
                      {shop.email}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="border-t border-border pt-3">
                  <div className="mb-1.5 flex items-center gap-1.5 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                    <Clock className="h-3 w-3" strokeWidth={1.5} />
                    {t("stores.openingHours", "Opening hours")}
                  </div>
                  <ul className="space-y-0.5 text-xs text-fg-secondary">
                    {shop.hours.map((h) => (
                      <li key={h.day} className="flex justify-between gap-2">
                        <span className="text-fg-tertiary">{h.day}</span>
                        <span>{h.hours}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Categories carried */}
                <div className="border-t border-border pt-3">
                  <div className="text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                    {t("stores.carries", "Carries")}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {shop.carries.map((c) => (
                      <Badge key={c} variant="default" size="sm">{c}</Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
